import './styles/modon-unified-theme.css';
import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './pages/Login';
import Register from './pages/Register';
import FactoryDashboard from './pages/FactoryDashboard';
import SDSEditorPage from './pages/SDSEditorPage';
import AdminDashboard from './pages/AdminDashboard';
import AdminStations from './pages/AdminStations';
import StationDashboard from './pages/StationDashboard';
import StationFireAccess from './pages/StationFireAccess';
import FireResponsePortal from './pages/FireResponsePortal';
import AdminSDSEditorPage from './pages/AdminSDSEditorPage';
import PDFViewerPage from './pages/PDFViewerPage';
import StationOriginalPDFViewer from './pages/StationOriginalPDFViewer';
import PublicDashboard from './pages/PublicDashboard';
import ExecutiveDashboard from './pages/ExecutiveDashboard';
import MapPage from './pages/MapPage';
import FactoryMyMap from './pages/FactoryMyMap';
import PublicSDSView from './pages/PublicSDSView';
import { Toaster } from './components/ui/sonner';
import { Loader2 } from 'lucide-react';
import './App.css';

function Protected({ children, requireRole }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin text-[#1a4d80]" size={32} />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (requireRole && user.role !== requireRole) {
    return <Navigate to={user.role === 'admin' ? '/admin' : user.role === 'station_manager' ? '/station' : '/factory'} replace />;
  }
  return children;
}

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to={user.role === 'admin' ? '/admin' : user.role === 'station_manager' ? '/station' : '/factory'} replace />;
  return <Navigate to="/login" replace />;
}

function PublicOrFactoryRedirect({ children, factoryTarget }) {
  // Public routes that should redirect logged-in FACTORY users away (admins still see them).
  const { user } = useAuth();
  if (user?.role === 'factory') return <Navigate to={factoryTarget} replace />;
  return children;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<RootRedirect />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/fire/:token" element={<FireResponsePortal />} />

          {/* Admin-only public statistics & map (no public access) */}
          <Route path="/dashboard" element={<Protected requireRole="admin"><PublicDashboard /></Protected>} />
          <Route path="/map" element={<Protected requireRole="admin"><MapPage /></Protected>} />
          <Route path="/sds/view/:id" element={<PublicSDSView />} />
          {/* Public executive TV dashboard (gated by an admin-controlled toggle) */}
          <Route path="/admin/tv" element={<ExecutiveDashboard />} />

          {/* Admin */}
          <Route path="/admin" element={<Protected requireRole="admin"><AdminDashboard /></Protected>} />
          <Route path="/admin/stations" element={<Protected requireRole="admin"><AdminStations /></Protected>} />
          <Route path="/station" element={<Protected requireRole="station_manager"><StationDashboard /></Protected>} />
          <Route path="/station/fire-access" element={<Protected requireRole="station_manager"><StationFireAccess /></Protected>} />
          <Route path="/station/sds/:id" element={<Protected requireRole="station_manager"><StationOriginalPDFViewer /></Protected>} />
          <Route path="/admin/sds/edit/:id" element={<Protected requireRole="admin"><AdminSDSEditorPage /></Protected>} />
          <Route path="/admin/sds/pdf/:id" element={<Protected requireRole="admin"><PDFViewerPage adminMode={true} /></Protected>} />

          {/* Factory */}
          <Route path="/factory" element={<Protected requireRole="factory"><FactoryDashboard /></Protected>} />
          <Route path="/factory/my-map" element={<Protected requireRole="factory"><FactoryMyMap /></Protected>} />
          <Route path="/factory/new" element={<Protected requireRole="factory"><SDSEditorPage /></Protected>} />
          <Route path="/factory/edit/:id" element={<Protected requireRole="factory"><SDSEditorPage /></Protected>} />
          <Route path="/factory/pdf/:id" element={<Protected requireRole="factory"><PDFViewerPage /></Protected>} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster position="top-center" richColors closeButton />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
